import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { UserGroups } from './groups/UserGroups'
import { PageHeader } from '@carrent/ui'
import { UserHistoryLink } from '../../components/HistoryLink'
import { StatusBadge } from '@carrent/ui'
import { adminApi, regionsApi, regionsAdminApi } from '@carrent/api'
import { useAuthStore } from '@carrent/core'
import { RegionPicker, RegionsMultiPicker, RegionBadge, RegionBadges } from '../../components/RegionPicker'
import { UserActivity, UserTasks } from './UserActivity'

// Секция активности прячет и заголовок: UserActivity возвращает null
// при 403, а заголовок без содержимого выглядел бы как поломка.
function UserActivitySection({ userId }) {
  const me = useAuthStore(st => st.user)
  if (me?.role !== 'admin') return null
  return (
    <>
      <p className="section-title">Активность</p>
      <div className="card" style={{ padding: 'var(--gap)' }}>
        <UserActivity userId={userId} />
      </div>
    </>
  )
}

// Фото с полным превью по клику
function PhotoCard({ label, path, required }) {
  const [open, setOpen] = useState(false)
  if (!path) return (
    <div style={{
      border: '2px dashed var(--border)', borderRadius: 'var(--r-sm)',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', padding: 'var(--gap-sm)', gap: 4,
      opacity: 0.5, minHeight: 100,
    }}>
      <span style={{ fontSize: '1.5rem' }}>📷</span>
      <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textAlign: 'center' }}>
        {label}{required ? ' *' : ''}<br/>не загружено
      </span>
    </div>
  )
  return (
    <>
      <div onClick={() => setOpen(true)} style={{ cursor: 'zoom-in', position: 'relative' }}>
        <img
          src={path} alt={label}
          style={{ width: '100%', borderRadius: 'var(--r-sm)',
            border: '1px solid var(--border)', height: '100%', objectFit: 'cover' }}
        />
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
          padding: '16px 8px 6px', borderRadius: '0 0 var(--r-sm) var(--r-sm)',
        }}>
          <p style={{ fontSize: '0.72rem', color: '#fff', margin: 0 }}>{label}</p>
        </div>
      </div>

      {/* Lightbox */}
      {open && (
        <div onClick={() => setOpen(false)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000, padding: 'var(--gap)', cursor: 'zoom-out',
        }}>
          <img src={path} alt={label}
            style={{ maxWidth: '100%', maxHeight: '90dvh', borderRadius: 'var(--r)',
              objectFit: 'contain' }} />
        </div>
      )}
    </>
  )
}

function InfoRow({ label, value }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 'var(--gap-sm)',
      padding: '10px var(--gap)', borderBottom: '1px solid var(--border)',
    }}>
      <span className="text-muted" style={{ minWidth: 130, fontSize: '0.82rem', flexShrink: 0 }}>
        {label}
      </span>
      <span style={{ fontSize: '0.9rem', wordBreak: 'break-word' }}>{value || '—'}</span>
    </div>
  )
}

export default function AdminUserDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [user, setUser]             = useState(null)
  const [loading, setLoading]       = useState(true)
  const [actionLoading, setActLoad] = useState(false)
  const [comment, setComment]       = useState('')
  const [reason, setReason]         = useState('')
  const [msg, setMsg]               = useState(null)  // {type: 'success'|'error', text}

  const load = () => {
    setLoading(true)
    adminApi.getUser(id)
      .then(r => setUser(r.data?.data))
      .catch(() => setMsg({ type: 'error', text: 'Не удалось загрузить пользователя' }))
      .finally(() => setLoading(false))
  }
  useEffect(load, [id])

  const act = async (fn, successMsg) => {
    setActLoad(true); setMsg(null)
    try {
      await fn()
      setMsg({ type: 'success', text: successMsg })
      setComment(''); setReason('')
      load()
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.message || 'Ошибка' })
    } finally { setActLoad(false) }
  }

  if (loading) return (
    <div>
      <PageHeader title="Загрузка..." backTo="/admin/users" />
      <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
        <div className="spinner" style={{ width: 40, height: 40 }} />
      </div>
    </div>
  )
  if (!user) return (
    <div>
      <PageHeader title="Не найдено" backTo="/admin/users" />
      <p className="p-page text-muted">Пользователь не найден</p>
    </div>
  )

  const photos = user.photos || {}
  const hasAnyPhoto = Object.values(photos).some(Boolean)

  return (
    <div style={{ paddingBottom: 'var(--gap-xl)' }}>
      <PageHeader
        title={user.full_name}
        backTo="/admin/users"
        right={
          <>
            <UserHistoryLink userId={user.id} label="" />
            <StatusBadge status={user.status} />
          </>
        }
      />

      <div className="p-page flex-col" style={{ gap: 'var(--gap)' }}>

        {/* Flash message */}
        {msg && (
          <div className={`alert alert--${msg.type === 'success' ? 'success' : 'error'}`}>
            {msg.text}
          </div>
        )}

        {/* Контакты */}
        <p className="section-title">Контактные данные</p>
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <InfoRow label="Телефон"  value={user.phone} />
          <InfoRow label="Email"    value={user.email} />
          <InfoRow label="Дата рожд." value={user.birth_date
            ? new Date(user.birth_date).toLocaleDateString('ru')
            : null} />
          <InfoRow label="Роль"     value={user.role} />
          <InfoRow label="Статус"   value={<StatusBadge status={user.status} />} />
          <InfoRow label="Зарегистрирован" value={
            new Date(user.created_at).toLocaleString('ru', { dateStyle: 'medium', timeStyle: 'short' })
          } />
        </div>

        {/* Задачи на пользователе: «что на нём висит» — раньше групп
            и паспорта, потому что это оперативный вопрос, а не справочный. */}
        <p className="section-title">Открытые задачи</p>
        <div className="card" style={{ padding: 'var(--gap)' }}>
          <UserTasks userId={user.id} />
        </div>

        {/* Активность из журнала аудита. У не-админа секции нет вовсе:
            журнал ему недоступен, и пустой заголовок вводил бы
            в заблуждение. */}
        <UserActivitySection userId={user.id} />

        {/* Группы базы знаний */}
        <p className="section-title">Группы</p>
        <div className="card">
          <UserGroups
            userId={user.id}
            userRegionIds={(user.regions || []).map((r) => r.id)}
          />
        </div>

        {/* Паспорт */}
        {(user.passport_number || user.passport_series) && (
          <>
            <p className="section-title">Паспортные данные</p>
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <InfoRow label="Серия / Номер" value={
                `${user.passport_series || ''}  ${user.passport_number || ''}`.trim()
              } />
              <InfoRow label="Кем выдан"    value={user.passport_issued_by} />
              <InfoRow label="Дата выдачи"  value={user.passport_issued_date
                ? new Date(user.passport_issued_date).toLocaleDateString('ru') : null} />
              <InfoRow label="Адрес"        value={user.passport_address} />
            </div>
          </>
        )}

        {/* Фото */}
        {hasAnyPhoto ? (
          <>
            <p className="section-title">Документы и фото</p>
            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 1fr',
              gap: 'var(--gap-sm)', gridAutoRows: '160px',
            }}>
              <PhotoCard label="Паспорт (разворот)" path={photos.passport_main}    required />
              <PhotoCard label="Паспорт (прописка)" path={photos.passport_address} />
              <PhotoCard label="Селфи"              path={photos.selfie}           required />
              <PhotoCard label="Селфи с паспортом"  path={photos.selfie_passport}  required />
            </div>
          </>
        ) : (
          <div className="card" style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 'var(--gap-lg)' }}>
            <div style={{ fontSize: '2rem', marginBottom: 8 }}>📷</div>
            <p style={{ fontSize: '0.88rem' }}>Фото не загружены</p>
          </div>
        )}

        {/* Комментарий от предыдущей проверки */}
        {user.admin_comment && (
          <div className="alert alert--info">
            <strong>Комментарий администратора:</strong> {user.admin_comment}
          </div>
        )}
        {user.blocked_reason && user.status === 'blocked' && (
          <div className="alert alert--error">
            <strong>Причина блокировки:</strong> {user.blocked_reason}
          </div>
        )}

        {/* ── ДЕЙСТВИЯ МОДЕРАЦИИ ─────────────────────────────── */}

        {/* Одобрить / Отклонить — для pending */}
        {user.status === 'pending' && (
          <>
            <p className="section-title">Решение по заявке</p>
            <div className="field">
              <label className="field__label">Комментарий</label>
              <textarea className="input" rows={2}
                placeholder="Необязательно для одобрения, обязательно для отклонения..."
                value={comment} onChange={e => setComment(e.target.value)}
                style={{ resize: 'vertical' }} />
            </div>
            <div style={{ display: 'flex', gap: 'var(--gap-sm)' }}>
              <button className="btn btn--danger" style={{ flex: 1 }}
                disabled={actionLoading || !comment.trim()}
                onClick={() => act(() => adminApi.rejectUser(id, comment), 'Заявка отклонена')}>
                {actionLoading ? <span className="spinner"/> : '❌ Отклонить'}
              </button>
              <button className="btn btn--primary" style={{ flex: 1 }}
                disabled={actionLoading}
                onClick={() => act(() => adminApi.approveUser(id, comment), '✅ Пользователь одобрен')}>
                {actionLoading ? <span className="spinner"/> : '✅ Одобрить'}
              </button>
            </div>
            <p className="text-muted" style={{ fontSize: '0.78rem', textAlign: 'center' }}>
              Для отклонения — обязательно укажите причину в комментарии
            </p>
          </>
        )}

        {/* Заблокировать — для approved */}
        {user.status === 'approved' && (
          <>
            <p className="section-title" style={{ color: 'var(--danger)' }}>Блокировка</p>
            <div className="field">
              <label className="field__label">Причина блокировки <span style={{color:'var(--danger)'}}>*</span></label>
              <textarea className="input" rows={2}
                placeholder="Укажите причину блокировки..."
                value={reason} onChange={e => setReason(e.target.value)}
                style={{ resize: 'vertical' }} />
            </div>
            <button className="btn btn--full" disabled={actionLoading || !reason.trim()}
              style={{ background: 'rgba(239,68,68,0.15)', color: 'var(--danger)', border: '1px solid rgba(239,68,68,0.4)' }}
              onClick={() => act(() => adminApi.blockUser(id, reason), 'Пользователь заблокирован')}>
              {actionLoading ? <span className="spinner"/> : '🚫 Заблокировать'}
            </button>
          </>
        )}

        {/* Разблокировать — для blocked */}
        {user.status === 'blocked' && (
          <>
            <p className="section-title">Управление</p>
            <button className="btn btn--primary btn--full"
              disabled={actionLoading}
              onClick={() => act(() => adminApi.unblockUser(id), '✅ Пользователь разблокирован')}>
              {actionLoading ? <span className="spinner"/> : '✅ Разблокировать'}
            </button>
          </>
        )}

        {/* Отклонённый — возможность одобрить или заблокировать */}
        {user.status === 'rejected' && (
          <>
            <p className="section-title">Управление</p>
            <div className="field">
              <label className="field__label">Комментарий</label>
              <textarea className="input" rows={2}
                placeholder="Комментарий к решению..."
                value={comment} onChange={e => setComment(e.target.value)}
                style={{ resize: 'vertical' }} />
            </div>
            <button className="btn btn--primary btn--full"
              disabled={actionLoading}
              onClick={() => act(() => adminApi.approveUser(id, comment), '✅ Пользователь одобрен')}>
              {actionLoading ? <span className="spinner"/> : '✅ Одобрить вручную'}
            </button>
          </>
        )}

        {/* ── Смена роли ───────────────────────────────────────────── */}
        <RoleSelector user={user} onChanged={load} />

        {/* ── Регион ───────────────────────────────────────────────── */}
        <RegionSelector user={user} onChanged={load} />

      </div>
    </div>
  )
}

// ── Компонент смены роли ──────────────────────────────────────────────────────
const ROLES = [
  { value: 'user',    label: 'Водитель',    icon: '🚗', desc: 'Может брать машины и совершать поездки' },
  { value: 'manager', label: 'Менеджер',    icon: '🔧', desc: 'Просмотр данных, без изменения ролей' },
  { value: 'admin',   label: 'Администратор', icon: '👑', desc: 'Полный доступ ко всем функциям' },
]

function RoleSelector({ user, onChanged }) {
  const { user: currentUser } = useAuthStore()
  const [selectedRole, setSelectedRole] = useState(user.role)
  const [loading, setLoading] = useState(false)
  const [msg, setMsg] = useState(null)

  // Нельзя менять свою роль
  const isSelf = currentUser?.id === user.id

  const handleSave = async () => {
    if (selectedRole === user.role) return
    setLoading(true); setMsg(null)
    try {
      await adminApi.changeRole(user.id, selectedRole)
      setMsg({ type: 'success', text: `Роль изменена на «${ROLES.find(r => r.value === selectedRole)?.label}»` })
      onChanged()
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.message || 'Ошибка смены роли' })
      setSelectedRole(user.role) // откат
    } finally { setLoading(false) }
  }

  return (
    <>
      <p className="section-title" style={{ marginTop: 'var(--gap)' }}>Роль пользователя</p>

      {isSelf && (
        <div className="alert alert--info" style={{ fontSize: '0.82rem' }}>
          Нельзя изменить собственную роль
        </div>
      )}

      {msg && (
        <div className={`alert alert--${msg.type === 'success' ? 'success' : 'error'}`}>
          {msg.text}
        </div>
      )}

      <div className="flex-col" style={{ gap: 'var(--gap-sm)' }}>
        {ROLES.map(role => (
          <button key={role.value} type="button"
            disabled={isSelf}
            onClick={() => !isSelf && setSelectedRole(role.value)}
            style={{
              display: 'flex', alignItems: 'center', gap: 'var(--gap-sm)',
              padding: 'var(--gap-sm) var(--gap)',
              border: `2px solid ${selectedRole === role.value ? 'var(--accent)' : 'var(--border)'}`,
              borderRadius: 'var(--r)',
              background: selectedRole === role.value ? 'rgba(59,130,246,0.08)' : 'var(--bg-card)',
              cursor: isSelf ? 'not-allowed' : 'pointer',
              opacity: isSelf ? 0.6 : 1,
              transition: 'all 0.15s',
              textAlign: 'left',
            }}>
            {/* Радиокнопка */}
            <div style={{
              width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
              border: `2px solid ${selectedRole === role.value ? 'var(--accent)' : 'var(--border)'}`,
              background: selectedRole === role.value ? 'var(--accent)' : 'transparent',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.15s',
            }}>
              {selectedRole === role.value && (
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff' }} />
              )}
            </div>
            <span style={{ fontSize: '1.2rem' }}>{role.icon}</span>
            <div>
              <p style={{
                fontWeight: 700, fontSize: '0.9rem',
                color: selectedRole === role.value ? 'var(--accent)' : 'var(--text)',
              }}>
                {role.label}
                {user.role === role.value && (
                  <span style={{ marginLeft: 6, fontSize: '0.72rem',
                    color: 'var(--text-muted)', fontWeight: 400 }}>
                    (текущая)
                  </span>
                )}
              </p>
              <p className="text-muted" style={{ fontSize: '0.75rem', marginTop: 1 }}>
                {role.desc}
              </p>
            </div>
          </button>
        ))}
      </div>

      {selectedRole !== user.role && !isSelf && (
        <button className="btn btn--primary btn--full" style={{ marginTop: 4 }}
          onClick={handleSave} disabled={loading}>
          {loading
            ? <span className="spinner" />
            : `Сохранить роль: ${ROLES.find(r => r.value === selectedRole)?.icon} ${ROLES.find(r => r.value === selectedRole)?.label}`
          }
        </button>
      )}
    </>
  )
}

// ── Селектор регионов (m2m) ──────────────────────────────────────────────────
function RegionSelector({ user, onChanged }) {
  const initialIds = (user.regions || []).map(r => r.id)
  const [selected, setSelected] = useState(initialIds)
  const [saving, setSaving]     = useState(false)
  const [msg, setMsg]           = useState(null)
  const me = useAuthStore(s => s.user)

  useEffect(() => {
    setSelected((user.regions || []).map(r => r.id))
  }, [user.regions])

  // Только super-admin может менять регионы
  if (me?.role !== 'admin' || (me?.regions && me.regions.length > 0)) return null

  const sortedSel = [...selected].sort()
  const sortedInit = [...initialIds].sort()
  const changed = JSON.stringify(sortedSel) !== JSON.stringify(sortedInit)

  const submit = async () => {
    setSaving(true); setMsg(null)
    try {
      await regionsAdminApi.setUserRegions(user.id, selected)
      setMsg({ type: 'success', text: 'Регионы обновлены' })
      onChanged?.()
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.message || 'Ошибка' })
    } finally { setSaving(false) }
  }

  return (
    <>
      <p className="section-title">Регионы доступа</p>
      <div className="card" style={{ padding: 'var(--gap)' }}>
        <p className="text-muted" style={{ fontSize: '0.78rem', marginBottom: 'var(--gap-sm)' }}>
          {user.role === 'admin'
            ? 'Если регионы не выбраны — администратор видит все.'
            : 'Пользователь видит данные только из выбранных регионов.'}
        </p>

        {(user.regions || []).length > 0 && (
          <div style={{ marginBottom: 'var(--gap-sm)' }}>
            <p className="text-muted" style={{ fontSize: '0.72rem', marginBottom: 4 }}>
              Текущие регионы:
            </p>
            <RegionBadges regions={user.regions} />
          </div>
        )}

        <RegionsMultiPicker value={selected} onChange={setSelected} label={null} />

        {msg && <div className={`alert alert--${msg.type}`} style={{ marginTop: 8 }}>{msg.text}</div>}

        {changed && (
          <button className="btn btn--primary btn--full"
            disabled={saving}
            onClick={submit}
            style={{ marginTop: 'var(--gap-sm)' }}>
            {saving ? <span className="spinner" /> : 'Применить'}
          </button>
        )}
      </div>
    </>
  )
}
